import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { SourceBadges } from "@/components/SourceBadges";

const inputCls = "mt-1 w-full bg-[#07090D] border border-[#1E293B] px-3 py-2 text-xs font-mono outline-none focus:border-[#14F195]/60 transition-colors";

const AxiomTokens = ({ connected, save }) => {
  const [t, setT] = useState({ axiom_access_token: "", axiom_refresh_token: "" });
  return (
    <div className="space-y-2 pt-3 border-t border-[#1E293B]" data-testid="axiom-tokens">
      <div className="text-xs text-slate-300">Axiom session · {connected
        ? <span className="text-[#14F195]" data-testid="axiom-connected">connected</span>
        : <span className="text-[#F59E0B]" data-testid="axiom-disconnected">not connected</span>}</div>
      <span className="text-[10px] text-slate-500 block">axiom.trade → F12 → Application → Cookies → copy auth-access-token & auth-refresh-token. Stored encrypted, auto-refreshed.</span>
      {["axiom_access_token", "axiom_refresh_token"].map((k) => (
        <input key={k} type="password" data-testid={`${k}-input`} value={t[k]} onChange={(e) => setT({ ...t, [k]: e.target.value })}
          placeholder={k.replace(/_/g, "-").replace("axiom-", "auth-")} className={inputCls} />
      ))}
      <div className="flex gap-2">
        <button data-testid="axiom-connect-btn" onClick={() => save(t).then(() => setT({ axiom_access_token: "", axiom_refresh_token: "" }))}
          disabled={!t.axiom_refresh_token && !t.axiom_access_token}
          className="px-3 py-1.5 bg-[#14F195] text-[#07090D] text-xs font-bold hover:bg-[#0fd884] disabled:opacity-40 transition-colors">Connect Axiom</button>
        {connected && <button data-testid="axiom-disconnect-btn" onClick={() => save({ disconnect_axiom: true })}
          className="px-3 py-1.5 border border-[#EF4444]/40 text-[#EF4444] text-xs hover:bg-[#EF4444]/10 transition-colors">Disconnect</button>}
      </div>
    </div>
  );
};

export const SourcesForm = ({ sources, reload }) => {
  const [f, setF] = useState({ leaderboard_window: sources.leaderboard_window, min_win_rate: sources.min_win_rate,
    min_pnl_usd: sources.min_pnl_usd, safety_checks: sources.safety_checks });
  const save = async (body) => {
    try {
      await api.post("/admin/sources", body);
      toast.success("Saved — refreshing traders");
      setTimeout(reload, 4000);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="panel p-5 space-y-3" data-testid="owner-sources">
      <div className="flex items-center justify-between gap-2"><div className="panel-title">Trader sources</div></div>
      <SourceBadges status={sources.status} />
      {sources.status?.axiom?.ok && (
        <div className="text-[10px] font-mono text-slate-400" data-testid="axiom-feed-info">
          Axiom live feed · {sources.status.axiom.migrations_24h ?? 0} graduated tokens (24h) · {sources.status.axiom.candidates ?? 0} wallets found · {sources.status.axiom.evaluated ?? 0} checked · {sources.status.axiom.count} pass {sources.min_win_rate}%+
        </div>
      )}
      {sources.status?.fomo?.ok && (
        <div className="text-[10px] font-mono text-slate-400" data-testid="fomo-feed-info">
          FOMO 24h board · top {sources.status.fomo.board ?? 0} wallets · {sources.status.fomo.evaluated ?? 0} checked · {sources.status.fomo.count} pass {sources.min_win_rate}%+ · pulled every 2h{sources.status.fomo.credits_remaining ? ` · ${Number(sources.status.fomo.credits_remaining).toLocaleString()} credits left` : ""}
        </div>
      )}
      {Object.entries(sources.status || {}).filter(([, s]) => !s.ok).map(([k, s]) => (
        <div key={k} className="text-[10px] font-mono text-[#F59E0B]" data-testid={`source-error-${k}`}>{k}: {s.error}</div>
      ))}
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-slate-300">Min 30D win rate %
          <input type="number" min="0" max="100" data-testid="min-win-rate-input" value={f.min_win_rate} onChange={(e) => setF({ ...f, min_win_rate: Number(e.target.value) })} className={inputCls} /></label>
        <label className="text-xs text-slate-300">Min 30D profit $
          <input type="number" min="0" data-testid="min-pnl-input" value={f.min_pnl_usd} onChange={(e) => setF({ ...f, min_pnl_usd: Number(e.target.value) })} className={inputCls} /></label>
      </div>
      <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
        <input type="checkbox" data-testid="safety-checks-toggle" checked={f.safety_checks} onChange={(e) => setF({ ...f, safety_checks: e.target.checked })} className="accent-[#14F195]" />
        Block unsafe tokens (mint/freeze authority, tax &gt;10%, top-10 &gt;70%, honeypot)
      </label>
      <button data-testid="sources-save-btn" onClick={() => save(f)} className="px-4 py-2 bg-[#14F195] text-[#07090D] text-xs font-bold hover:bg-[#0fd884] transition-colors">Save</button>
      <AxiomTokens connected={sources.axiom_connected} save={save} />
    </div>
  );
};
